import { useId, useState } from 'react';
import { XIcon } from 'lucide-react';

import { cn } from '@mds/shared/lib/utils';

import Button from '@/components/ui/button';
import { FieldError, inputCls } from '@/components/ui/field';

/**
 * A small set of labels — tags, interests — picked from a suggested pool or
 * typed in.
 *
 * The pool is a shortcut, not a closed list: the front desk meets people who do
 * not fit the eight words someone picked in advance, and a label nobody can add
 * is a label that ends up in the notes field instead.
 */
export default function ChipPicker({
  label,
  pool,
  value,
  onChange,
  placeholder = 'Add another…',
  error,
  onBlur,
}: {
  label: string;
  pool: readonly string[];
  value: string[];
  onChange: (next: string[]) => void;
  placeholder?: string;
  /** A problem with the list as a whole, or with one of its labels. */
  error?: string;
  onBlur?: () => void;
}) {
  const errorId = useId();
  const [draft, setDraft] = useState('');

  const has = (item: string) =>
    value.some((selected) => selected.toLowerCase() === item.toLowerCase());

  const addItem = (item: string) => {
    if (!has(item)) onChange([...value, item]);
  };

  const removeItem = (item: string) => onChange(value.filter((selected) => selected !== item));

  const add = () => {
    const next = draft.trim();
    // Case-insensitive, or the same label arrives twice in two spellings and
    // the filters treat them as different things.
    if (next) addItem(next);
    setDraft('');
  };

  const suggestions = pool.filter((item) => !has(item));

  return (
    <fieldset className="m-0 border-0 p-0" onBlur={onBlur}>
      <legend
        className={cn(
          'font-mono text-sm font-semibold tracking-wider uppercase',
          error ? 'text-danger-text' : 'text-ink-secondary',
        )}
      >
        {label}
      </legend>

      {value.length === 0 ? (
        <p className="mt-3 mb-0 text-base text-ink-muted">None selected.</p>
      ) : (
        <div className="mt-3">
          <p className="m-0 text-sm text-ink-muted">Selected. Use × to remove.</p>
          <ul className="m-0 mt-2 flex list-none flex-wrap gap-2 p-0">
            {value.map((item) => (
              <li
                key={item}
                className="inline-flex min-h-(--target-min) items-center gap-1 rounded-full border border-brand-edge bg-brand-wash py-1 pr-1 pl-3.5 font-semibold text-brand-strong"
              >
                <span>{item}</span>
                <button
                  type="button"
                  onClick={() => removeItem(item)}
                  aria-label={`Remove ${item} from ${label}`}
                  title={`Remove ${item}`}
                  className="inline-flex size-9 items-center justify-center rounded-full text-brand-strong transition-colors hover:bg-surface focus-visible:outline-(length:--focus-ring-width) focus-visible:outline-offset-(--focus-ring-offset) focus-visible:outline-(color:--focus-ring-color)"
                >
                  <XIcon aria-hidden="true" className="size-4" />
                </button>
              </li>
            ))}
          </ul>
        </div>
      )}

      {suggestions.length > 0 && (
        <div className="mt-3">
          <p className="m-0 text-sm text-ink-muted">Suggestions</p>
          <ul className="m-0 mt-2 flex list-none flex-wrap gap-2 p-0">
            {suggestions.map((item) => (
              <li key={item}>
                <button
                  type="button"
                  onClick={() => addItem(item)}
                  className={cn(
                    'min-h-(--target-min) rounded-full border border-hairline bg-surface px-3.5 py-1.5 text-base text-ink-muted transition-colors hover:border-brand-edge hover:text-brand-text',
                    'focus-visible:outline-(length:--focus-ring-width) focus-visible:outline-offset-(--focus-ring-offset) focus-visible:outline-(color:--focus-ring-color)',
                  )}
                >
                  {item}
                </button>
              </li>
            ))}
          </ul>
        </div>
      )}

      <div className="mt-3 flex items-stretch gap-2">
        <input
          className={inputCls}
          placeholder={placeholder}
          aria-label={`Add to ${label}`}
          aria-invalid={error ? true : undefined}
          aria-describedby={error ? errorId : undefined}
          value={draft}
          onChange={(e) => setDraft(e.target.value)}
          onKeyDown={(e) => {
            if (e.key !== 'Enter') return;
            // The field sits inside a dialog; Enter here means "add", not "save".
            e.preventDefault();
            add();
          }}
        />
        <Button variant="quiet" className="h-auto" onClick={add} disabled={!draft.trim()}>
          Add
        </Button>
      </div>
      {error && (
        <div className="mt-2">
          <FieldError id={errorId}>{error}</FieldError>
        </div>
      )}
    </fieldset>
  );
}
